import React, { useEffect, useState } from 'react';
import { Button, Link } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import {
  CustomizableInfoValueCollection,
  InfoValueGroup,
  InfoValueRow
} from '../FormWidgets/InfoValueList';
import { NormalValue } from '../FormWidgets/InfoValues';
import Desc from '../FormWidgets/Description';
import { IsExcludedBasedOnTypeOrProject, getControllerApiVersion } from '../Utils/utils';
import { supportsNutritionTanks } from '../Utils/nutrition_utils';
import {
  tankMeasurements,
  tankLevelKeys,
  tankMeasurementValue,
  tankSetpointValue,
  tankRemainingTime,
  tankSourceZone,
  tankOwnsSource
} from '../Utils/tank_monitoring';

const states = [
  'disabled',
  'idle',
  'queued',
  'settling',
  'filling',
  'retry_wait',
  'blocked',
  'stopping',
  'held'
];
const faults = [
  'none',
  'level_missing',
  'level_invalid',
  'route_unavailable',
  'timeout',
  'output_unavailable',
  'persistence'
];

export function getNutritionTankInfo(controller, parent) {
  if (
    !supportsNutritionTanks(controller) ||
    !controller.access_rights?.nutrition?.read ||
    IsExcludedBasedOnTypeOrProject(controller, 'nutrition', 'tabs')
  )
    return [];
  // Earlier Tank Info formats carry names/eligibility only, without live fill status.
  return (parent.settings?.irrigation_tank_info ?? []).filter(
    (tank) => Number.isInteger(tank.owner_zone) && Number.isInteger(tank.state)
  );
}

export const hasNutritionTankInfo = (controller, parent) =>
  getNutritionTankInfo(controller, parent).length > 0;

function TankInfoGroup({ title, expandId, summary, children }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(
    () => localStorage.getItem(`expandable_${expandId}`) === 'true'
  );
  useEffect(() => {
    localStorage.setItem(`expandable_${expandId}`, expanded.toString());
  }, [expandId, expanded]);
  return (
    <InfoValueGroup title={title}>
      {summary}
      {expanded && children}
      <InfoValueRow label={t('more')}>
        <Button
          aria-expanded={expanded}
          aria-label={t('more')}
          color="primary"
          size="small"
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? '▲' : '▼'}
        </Button>
      </InfoValueRow>
    </InfoValueGroup>
  );
}

export default function NutritionTankInfo({
  controller,
  parent,
  renderMeasurementDetail,
  orphan = false,
  paired
}) {
  const { t } = useTranslation();
  const snapshot = useSnapshot(parent);
  const controllerSnapshot = useSnapshot(controller);
  const liveTanks = getNutritionTankInfo(controllerSnapshot, snapshot);
  const unknownCode = (code, key = 'unknown') =>
    `${t(key)}${Number.isInteger(code) ? ` (${code})` : ''}`;
  if (!liveTanks.length) return null;
  return (
    <CustomizableInfoValueCollection
      controller={controller}
      parent={parent}
      id="nutrition_tanks"
      orphan={orphan}
      paired={paired ?? liveTanks.length > 1}
    >
      {liveTanks.map((tank) => {
        const owner = tank.owner_zone;
        const ownerParent =
          owner === 254 || (!controllerSnapshot.zones && owner === 0)
            ? controllerSnapshot
            : controllerSnapshot.zones?.[owner];
        const routes = ownerParent?.settings?.nutrition?.routes ?? [];
        const route = routes.find((route) => route.route_id === tank.active_route_id);
        const sourceZone = tank.measurement_source_zone;
        const sourceParent = controllerSnapshot.zones
          ? controllerSnapshot.zones[sourceZone]
          : controllerSnapshot;
        const localSource = tankOwnsSource(
          tank,
          tankSourceZone(parent, Boolean(controllerSnapshot.zones)),
          tank.measurement_source_index
        );
        const rows = [
          [
            'tank_fill_state',
            states[tank.state]
              ? t(`tank_state_${states[tank.state]}`)
              : unknownCode(tank.state, 'tank_state_unknown')
          ],
          [
            'tank_active_route',
            tank.active_route_id === 0
              ? t('tank_no_active_route')
              : route && tank.active_route_id <= 31
                ? `${route.name} (${tank.active_route_id})`
                : unknownCode(tank.active_route_id)
          ],
          [
            'tank_fault',
            faults[tank.fault]
              ? t(
                  tank.fault === 4 &&
                    controllerSnapshot.type === 5155 &&
                    getControllerApiVersion(controllerSnapshot).localeCompare('BAICA') >= 0
                    ? 'tank_fault_min_not_reached'
                    : `tank_fault_${faults[tank.fault]}`
                )
              : unknownCode(tank.fault, 'tank_fault_unknown')
          ],
          ['tank_irrigation_time_remaining', tankRemainingTime(tank)]
        ];
        if (Number.isInteger(tank.enabled) && tank.enabled !== 0 && tank.enabled !== 1)
          rows.push(['tank_enabled', unknownCode(tank.enabled)]);
        let levelRow = ['tank_level', t('tank_level_unknown')];
        if (Number.isInteger(tank.level)) {
          const percent = tank.level_percent;
          const measuredPercent = Number.isInteger(percent) && percent >= 0 && percent <= 100;
          const level = tankLevelKeys[tank.level]
            ? t(tankLevelKeys[tank.level])
            : unknownCode(tank.level, 'tank_level_unknown');
          const unknownPercent = Number.isInteger(percent) && !measuredPercent && percent !== 255;
          levelRow = [
            'tank_level',
            unknownPercent
              ? `${unknownCode(percent)} · ${level}`
              : measuredPercent && tank.level !== 4 && tank.level !== 5
                ? `${percent}% · ${level}`
                : level
          ];
        }
        const measurements = tankMeasurements.map((measurement, index) => {
          const hasSetpoint = index < 3 && Boolean(tank.setpoint_valid_mask & (1 << index));
          const configured = Boolean(tank.measurement_configured_mask & (1 << index));
          if (index >= 3 && !configured) return null;
          const value = tankMeasurementValue(tank, index);
          const setpoint = tankSetpointValue(tank, index);
          return (
            <InfoValueRow
              key={measurement.key}
              label={<Desc id={measurement.key} />}
              error={configured && value === null}
              setpoint={
                setpoint !== null ? `${t('setpoint_info')} ${setpoint} ${measurement.unit}` : null
              }
              valueDetail={renderMeasurementDetail?.(tank, measurement)}
            >
              <NormalValue
                value={
                  value === null
                    ? configured || hasSetpoint
                      ? t('tank_measurement_unavailable')
                      : '---'
                    : value
                }
                unit={measurement.unit}
              />
            </InfoValueRow>
          );
        });
        const expandId = `${controllerSnapshot.id}:${parent.zone_id ?? parent.id ?? 254}:info_tank_more_${tank.tank_id}`;
        return (
          <TankInfoGroup
            key={expandId}
            expandId={expandId}
            title={tank.name || t('tank_id', { id: tank.tank_id })}
            summary={
              <>
                <InfoValueRow label={t(levelRow[0])} error={tank.level === 4}>
                  <NormalValue value={levelRow[1]} />
                </InfoValueRow>
                {measurements.slice(0, 3)}
              </>
            }
          >
            {measurements.slice(3)}
            {!localSource &&
              sourceParent &&
              tankOwnsSource(tank, sourceZone, tank.measurement_source_index) && (
                <InfoValueRow label={t('sensors')}>
                  <Link
                    href={`#/controller/${controllerSnapshot.id}/zones/zone_${sourceZone}/info`}
                  >
                    {sourceParent.name || `${t('zone')} ${sourceZone + 1}`}
                  </Link>
                </InfoValueRow>
              )}
            {rows.map(([label, value]) => (
              <InfoValueRow
                key={label}
                label={label === 'tank_irrigation_time_remaining' ? <Desc id={label} /> : t(label)}
                error={label === 'tank_fault' && tank.fault > 0}
              >
                <NormalValue value={value} style={{ whiteSpace: 'normal' }} />
              </InfoValueRow>
            ))}
          </TankInfoGroup>
        );
      })}
    </CustomizableInfoValueCollection>
  );
}
